import CreditFactor, {
  CreditFactorCanada,
  CreditFactorUsa,
} from "@repo/kikoff-models/credit/CreditFactor";
import CreditReport, {
  CreditReportCanada,
  CreditReportUsa,
} from "@repo/kikoff-models/credit/CreditReport";
import CreditScore, {
  CreditScoreCanada,
  CreditScoreUsa,
} from "@repo/kikoff-models/credit/CreditScore";
import ReportAccount from "@repo/kikoff-models/credit/ReportAccount";
import t from "@repo/localization/string.macro";
import canadaRpc from "@repo/protobuf/api/canadaRpc";
import usRpc from "@repo/protobuf/api/usRpc";
import { CreditV2 } from "@repo/protobuf/gen/web/public/credit_pb";
import UArray from "@repo/utils/UArray";
import UObject from "@repo/utils/UObject";
import UProxy from "@repo/utils/UProxy";
import FeatureStore from "@repo/utils-solid/FeatureStore";

const defaultBureau = "equifax";

export namespace Credit {
  export const bureaus = ["equifax", "experian", "transunion"] as const;
  export type Bureau = (typeof bureaus)[number];

  export namespace Bureau {
    export type State = {
      latestScore?: CreditScore;
      scoreChange?: number;
      scoreHistory?: CreditScore[];
      factors?: CreditFactor[];
      report?: CreditReport;
    };
    export const format = (() => {
      const map = {
        equifax: () => t`Equifax`,
        experian: () => t`Experian`,
        transunion: () => t`Transunion`,
      } satisfies Record<Bureau, () => string>;
      return (bureau: Bureau) => map[bureau]();
    })();
  }
}

const initialState = {
  byBureau: {} as Partial<Record<Credit.Bureau, Credit.Bureau.State>>,
};

export const { Provider, useCredit, Context } = FeatureStore.init(
  "Credit",
  initialState,
  ([store, setStore], { attachAction, withLogger }) => {
    const accessors = UProxy.get(
      [...Credit.bureaus, "default"] as const,
      (bureau) => {
        bureau = bureau === "default" ? defaultBureau : bureau;
        const bureauReport = attachAction(
          () => store.byBureau[bureau],
          () => actions.fetchReport(bureau),
          () => () => store.byBureau[bureau]?.report
        );

        return {
          loaded: () => !!store.byBureau[bureau],
          latestScore: attachAction(
            () => store.byBureau[bureau]?.latestScore,
            () => actions.fetchScore(bureau)
          ),
          scoreChange: attachAction(
            () => store.byBureau[bureau]?.scoreChange,
            () => actions.fetchScore(bureau)
          ),
          scoreHistory: () => bureauReport()?.scoreHistory,
          report: () => bureauReport()?.report,
          factors: () => bureauReport()?.factors,
          factorByType: <T extends CreditFactor.Type>(type: T) =>
            bureauReport()?.factors?.find((f) => f.type === type) as
              | UObject.Override<
                  CreditFactor,
                  { details: CreditFactor.ByType[T] }
                >
              | undefined,
          accountById: (id: ReportAccount.Id) =>
            bureauReport()?.report?.accounts.find(
              (account) => account.id === id
            ),
        };
      }
    );

    const mutations = {
      bureau(bureau: Credit.Bureau, state: Credit.Bureau.State) {
        setStore("byBureau", bureau, state);
      },
    };

    const us = {
      mutations: {
        setBureau(creditV2: CreditV2) {
          setStore("byBureau", "equifax", {
            latestScore: CreditScoreUsa.normalize(creditV2.currentCreditScore),
            scoreHistory: creditV2.creditScoreHistory?.creditScores.map(
              CreditScoreUsa.normalize
            ),
            factors: creditV2.factors?.map((factor) =>
              CreditFactorUsa.normalize(factor)
            ),
            report: CreditReportUsa.normalize(creditV2),
          });
        },
      },
      actions: {
        fetchScore: (bureau: Credit.Bureau) => us.actions.fetchReport(bureau),
        fetchReport(bureau: Credit.Bureau) {
          if (bureau !== "equifax")
            throw new Error(
              `Attempted to fetch unsupported bureau "${bureau}"`
            );

          return usRpc.Credit.getCredit({})
            .SUCCESS((data) => {
              us.mutations.setBureau(data.credit!);
            })
            .elseThrow(t`Failed to fetch credit report.`);
        },
      },
    };

    const canada = {
      actions: {
        fetchScore(bureau: Credit.Bureau | "default") {
          bureau = bureau === "default" ? defaultBureau : bureau;
          if (bureau !== "equifax")
            throw new Error(
              `Attempted to fetch score from unsupported bureau "${bureau}"`
            );

          return canadaRpc.Credit.showScore({}).then(
            ({ score, scoreChange }) => {
              mutations.bureau(bureau, {
                latestScore: CreditScoreCanada.normalize(score),
                // TODO: Ensure scoreChange is 0 if this is the first score
                scoreChange: scoreChange === score?.score ? 0 : scoreChange,
              });
            }
          );
        },
        fetchReport(bureau: Credit.Bureau) {
          if (bureau !== "equifax")
            throw new Error(
              `Attempted to fetch report from unsupported bureau "${bureau}"`
            );

          return Promise.all([
            canadaRpc.Credit.showReport({}),
            // TODO: Include current score in report
            canada.actions.fetchScore(bureau),
          ]).then(([{ report, scoreHistory, factors }]) => {
            if (!report) return mutations.bureau(bureau, {});

            mutations.bureau(bureau, {
              scoreChange: (() => {
                if (scoreHistory.length < 2) return 0;

                const [prev, current] = UArray.assertLengthOf.atLeast(
                  scoreHistory.slice(-2),
                  2
                );
                return current.score - prev.score;
              })(),
              report: CreditReportCanada.normalize(report!),
              scoreHistory: scoreHistory.map((creditScore) =>
                CreditScoreCanada.normalize(creditScore)
              ),
              factors: factors.map(CreditFactorCanada.normalize),
            });
          });
        },
      },
    };

    const actions = withLogger(
      "actions",
      import.meta.env.API_ADAPTER === "canada" ? canada.actions : us.actions
    );

    return { accessors, actions };
  }
);
